import React, { useState, useEffect } from 'react';
import { FaTimes, FaRupeeSign, FaCheckCircle, FaCalendarAlt, FaCreditCard, FaInfoCircle, FaTrash } from 'react-icons/fa';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { formatDateLocale } from '../utils/dateFormatter';
import { formatCurrency } from '../utils/currencyFormatter';

/**
 * Payment Plan Assignment Modal Component
 * Allows trainers to assign, change, or remove payment plans for students
 */
function PaymentPlanAssignmentModal({ isOpen, onClose, studentId, studentName, programId, currentPlanId, totalAmount, onPlanAssigned }) {
  const [installmentPlans, setInstallmentPlans] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(currentPlanId || '');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (isOpen && programId) {
      fetchInstallmentPlans();
      if (currentPlanId) {
        fetchPaymentSchedule();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, programId, currentPlanId]);

  const fetchInstallmentPlans = async () => {
    try {
      setIsLoadingPlans(true);
      const response = await HttpInterceptor.get('/api/fee-plan/installments', {
        params: { programId }
      });
      const plans = Array.isArray(response.data) ? response.data : [];
      // Filter plans for the specific program
      const programPlans = plans.filter(plan => plan.programId === programId);
      setInstallmentPlans(programPlans);
    } catch (error) {
      console.error('Error fetching installment plans:', error);
      toast.error('Failed to load payment plans');
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const fetchPaymentSchedule = async () => {
    try {
      const response = await HttpInterceptor.get(`/api/fee-plan/students/${studentId}/payment-schedule`);
      if (response.data?.schedule) {
        setPaymentSchedule(response.data.schedule);
      }
    } catch (error) {
      console.error('Error fetching payment schedule:', error);
    }
  };

  const handlePlanChange = (planId) => {
    setSelectedPlanId(planId);
    if (planId) {
      // Calculate schedule for selected plan
      const plan = installmentPlans.find(p => p._id === planId);
      if (plan && plan.intervals) {
        const schedule = plan.intervals.map((interval, index) => {
          const amount = (totalAmount * interval.percentage) / 100;
          return {
            installmentNumber: index + 1,
            dueDate: interval.dueDate,
            amount: amount,
            percentage: interval.percentage,
            status: 'pending'
          };
        });
        setPaymentSchedule(schedule);
      }
    } else {
      setPaymentSchedule([]);
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a payment plan');
      return;
    }

    try {
      setIsAssigning(true);
      await HttpInterceptor.post(`/api/fee-plan/students/${studentId}/assign-plan`, {
        paymentPlanId: selectedPlanId
      });
      toast.success('Payment plan assigned successfully');
      if (onPlanAssigned) {
        onPlanAssigned();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning payment plan:', error);
      toast.error(error.response?.data?.message || 'Failed to assign payment plan');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemovePlan = async () => {
    try {
      setIsAssigning(true);
      // Remove plan by assigning null or empty
      await HttpInterceptor.post(`/api/fee-plan/students/${studentId}/assign-plan`, {
        paymentPlanId: null
      });
      toast.success('Payment plan removed successfully');
      if (onPlanAssigned) {
        onPlanAssigned();
      }
      onClose();
    } catch (error) {
      console.error('Error removing payment plan:', error);
      // If backend doesn't support null, try delete endpoint
      toast.error(error.response?.data?.message || 'Failed to remove payment plan');
    } finally {
      setIsAssigning(false);
    }
  };

  if (!isOpen) return null;

  const selectedPlan = installmentPlans.find(p => p._id === selectedPlanId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Assign Payment Plan</h2>
            <p className="text-sm text-gray-600 mt-1">{studentName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-2"
            aria-label="Close modal"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoadingPlans ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading payment plans...</p>
            </div>
          ) : (
            <>
              {/* Current Plan Info */}
              {currentPlanId && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-800">Current Plan</p>
                      <p className="text-lg font-semibold text-blue-900 mt-1">
                        {installmentPlans.find(p => p._id === currentPlanId)?.name || 'Active Plan'}
                      </p>
                    </div>
                    <button
                      onClick={handleRemovePlan}
                      disabled={isAssigning}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaTrash />
                      Remove Plan
                    </button>
                  </div>
                </div>
              )}

              {/* Plan Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Payment Plan
                </label>
                <div className="space-y-3">
                  {/* One-time Payment Option */}
                  <div
                    onClick={() => handlePlanChange('')}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedPlanId === ''
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">One-time Payment</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Pay full amount: <span className="font-medium">{formatCurrency(totalAmount)}</span>
                        </p>
                      </div>
                      {selectedPlanId === '' && (
                        <FaCheckCircle className="text-blue-600 text-xl" />
                      )}
                    </div>
                  </div>

                  {/* Installment Plans */}
                  {installmentPlans.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No installment plans available for this program</p>
                    </div>
                  ) : (
                    installmentPlans.map((plan) => (
                      <div
                        key={plan._id}
                        onClick={() => handlePlanChange(plan._id)}
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          selectedPlanId === plan._id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                {plan.numberOfInstallments || plan.intervals?.length || 0} installments
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                              <div>
                                <p className="text-gray-600">Amount per installment:</p>
                                <p className="font-semibold text-gray-900">
                                  {formatCurrency(plan.intervals && plan.intervals.length > 0
                                    ? Math.round((totalAmount * plan.intervals[0].percentage) / 100)
                                    : Math.round(totalAmount / (plan.numberOfInstallments || 1)))}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Total amount:</p>
                                <p className="font-semibold text-gray-900">
                                  {formatCurrency(totalAmount)}
                                </p>
                              </div>
                            </div>
                          </div>
                          {selectedPlanId === plan._id && (
                            <FaCheckCircle className="text-blue-600 text-xl ml-4" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Payment Schedule Preview */}
              {selectedPlan && selectedPlan.intervals && paymentSchedule.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Schedule</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Percentage</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paymentSchedule.map((installment, index) => (
                            <tr key={index}>
                              <td className="px-4 py-3 text-sm text-gray-900">{installment.installmentNumber}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                <div className="flex items-center">
                                  <FaCalendarAlt className="mr-2 text-gray-400" />
                                  {formatDateLocale(installment.dueDate)}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                {formatCurrency(installment.amount)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{installment.percentage}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Message */}
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-start">
                  <FaInfoCircle className="text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-1">Note:</p>
                    <p>Assigning a payment plan will help track installment payments. You can change or remove the plan at any time.</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssignPlan}
            disabled={isAssigning || !selectedPlanId || selectedPlanId === currentPlanId}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAssigning ? 'Assigning...' : selectedPlanId === currentPlanId ? 'Already Assigned' : 'Assign Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentPlanAssignmentModal;

